package com.speedsoftware.nibbler.ui.boards

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.PrimaryScrollableTabRow
import androidx.compose.material3.PrimaryTabRow
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRowDefaults
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.AddMemberRequest
import com.speedsoftware.nibbler.models.BoardDto
import com.speedsoftware.nibbler.models.CreateBoardRequest
import com.speedsoftware.nibbler.models.CreateSprintRequest
import com.speedsoftware.nibbler.models.CreateTaskRequest
import com.speedsoftware.nibbler.models.IssueType
import com.speedsoftware.nibbler.models.ProjectDto
import com.speedsoftware.nibbler.models.ProjectRole
import com.speedsoftware.nibbler.models.SprintDto
import com.speedsoftware.nibbler.models.SprintStatus
import com.speedsoftware.nibbler.models.TaskDto
import com.speedsoftware.nibbler.models.TaskPriority
import com.speedsoftware.nibbler.models.BoardColumnDto
import com.speedsoftware.nibbler.ui.components.AssigneeAvatarStack
import com.speedsoftware.nibbler.ui.components.PropertyIcon
import com.speedsoftware.nibbler.ui.components.PropertyIconView
import com.speedsoftware.nibbler.models.CreateColumnRequest
import com.speedsoftware.nibbler.models.UpdateColumnRequest
import com.speedsoftware.nibbler.models.UpdateBoardRequest
import com.speedsoftware.nibbler.models.UpdateSprintRequest
import com.speedsoftware.nibbler.models.UpdateTaskRequest
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.ProjectTab
import com.speedsoftware.nibbler.ui.Screen
import com.speedsoftware.nibbler.ui.components.IssueTypeBadge
import com.speedsoftware.nibbler.ui.components.PriorityBadge
import com.speedsoftware.nibbler.ui.components.ResponsiveScaffold
import com.speedsoftware.nibbler.ui.components.SprintStatusBadge
import com.speedsoftware.nibbler.ui.components.VersionTag
import com.speedsoftware.nibbler.ui.docs.DocumentViewerScreen
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun ProjectBoardScreen(
    appState: AppState,
    initialProject: ProjectDto
) {
    var project by remember { mutableStateOf(initialProject) }
    // The project list carries no boards; until the first full load arrives we show a spinner, not an empty state
    var hasLoaded by remember { mutableStateOf(initialProject.boards.isNotEmpty()) }
    var selectedBoardIndex by remember { mutableStateOf(0) }

    // Sprint Filter (null = All Tasks, otherwise specific sprintId)
    var selectedSprintFilter by remember { mutableStateOf<String?>(null) }

    // Search and filter
    var searchQuery by remember { mutableStateOf("") }
    var selectedTypeFilter by remember { mutableStateOf<IssueType?>(null) }
    var selectedPriorityFilter by remember { mutableStateOf<TaskPriority?>(null) }
    var onlyMyIssues by remember { mutableStateOf(false) }

    // Dialog states
    var showCreateTaskDialog by remember { mutableStateOf(false) }
    var showCreateBoardDialog by remember { mutableStateOf(false) }
    var showEditBoardDialog by remember { mutableStateOf(false) }
    var showSprintManagerDialog by remember { mutableStateOf(false) }
    var showAddMemberDialog by remember { mutableStateOf(false) }
    var editingTask by remember { mutableStateOf<TaskDto?>(null) }
    var showAddColumnDialog by remember { mutableStateOf(false) }
    var editingColumn by remember { mutableStateOf<BoardColumnDto?>(null) }
    var deletingColumn by remember { mutableStateOf<BoardColumnDto?>(null) }

    // New/Edit task form fields
    var taskTitle by remember { mutableStateOf("") }
    var taskDescription by remember { mutableStateOf("") }
    var taskType by remember { mutableStateOf(IssueType.TASK) }
    var taskPriority by remember { mutableStateOf(TaskPriority.MEDIUM) }
    var taskStatus by remember { mutableStateOf("") }
    var taskVersion by remember { mutableStateOf("") }
    var taskSprintId by remember { mutableStateOf<String?>(null) }
    var taskAssigneeIds by remember { mutableStateOf<List<String>>(emptyList()) }

    // Board form fields
    var boardName by remember { mutableStateOf("") }
    var boardDescription by remember { mutableStateOf("") }

    // Sprint form fields
    var newSprintName by remember { mutableStateOf("") }
    var newSprintGoal by remember { mutableStateOf("") }
    var newSprintStartDate by remember { mutableStateOf("") }
    var newSprintEndDate by remember { mutableStateOf("") }

    // Add member form fields
    var memberEmail by remember { mutableStateOf("") }
    var memberRole by remember { mutableStateOf(ProjectRole.DEVELOPER) }

    val scope = rememberCoroutineScope()
    val dragDropState = remember { KanbanDragDropState() }

    fun refreshProject() {
        scope.launch {
            appState.apiClient.getProjectDetails(project.id).onSuccess {
                project = it
                hasLoaded = true
            }
        }
    }

    LaunchedEffect(project.id) {
        refreshProject()
    }

    fun openCreateIssue(statusId: String = "") {
        taskTitle = ""
        taskDescription = ""
        taskType = IssueType.TASK
        taskPriority = TaskPriority.MEDIUM
        taskStatus = statusId
        taskVersion = ""
        taskSprintId = selectedSprintFilter.takeIf { it != "BACKLOG" }
        taskAssigneeIds = emptyList()
        showCreateTaskDialog = true
    }

    ResponsiveScaffold(
        appState = appState,
        title = project.name,
        subtitle = project.boards.getOrNull(selectedBoardIndex)?.name?.takeIf { appState.selectedProjectTab == ProjectTab.BOARDS },
        onNewIssueClick = { openCreateIssue() },
        actions = {
            if (appState.selectedProjectTab == ProjectTab.BOARDS && project.boards.isNotEmpty()) {
                Row(
                    modifier = Modifier
                        .height(38.dp)
                        .background(NibblerColors.Accent, RoundedCornerShape(10.dp))
                        .clickable { openCreateIssue() }
                        .padding(horizontal = 14.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    PropertyIconView(PropertyIcon.PLUS, size = 14.dp, color = Color.White)
                    Text("Create issue", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                }
            }
        }
    ) {
        Column(modifier = Modifier.fillMaxSize().background(NibblerColors.Background)) {
            when (appState.selectedProjectTab) {
                ProjectTab.BOARDS -> {
                    if (project.boards.isNotEmpty()) {
                        val statsBoard = project.boards[selectedBoardIndex.coerceIn(0, project.boards.lastIndex)]
                        val doneColumnId = statsBoard.columns.lastOrNull()?.id
                        val donePercent = if (statsBoard.tasks.isEmpty()) null
                        else statsBoard.tasks.count { it.status == doneColumnId } * 100 / statsBoard.tasks.size

                        BoardSwitcher(
                            boards = project.boards,
                            selectedIndex = selectedBoardIndex.coerceIn(0, project.boards.lastIndex),
                            sprintCount = project.sprints.size,
                            onSelect = { selectedBoardIndex = it },
                            onAddBoard = {
                                boardName = ""
                                boardDescription = ""
                                showCreateBoardDialog = true
                            },
                            onEditBoard = {
                                boardName = statsBoard.name
                                boardDescription = statsBoard.description
                                showEditBoardDialog = true
                            },
                            onSprints = { showSprintManagerDialog = true }
                        )
                        QuickFilters(
                            searchQuery = searchQuery,
                            onSearch = { searchQuery = it },
                            typeFilter = selectedTypeFilter,
                            onType = { selectedTypeFilter = it },
                            priorityFilter = selectedPriorityFilter,
                            onPriority = { selectedPriorityFilter = it },
                            sprintFilter = selectedSprintFilter,
                            sprints = project.sprints,
                            onSprint = { selectedSprintFilter = it },
                            onlyMine = onlyMyIssues,
                            onOnlyMine = { onlyMyIssues = it },
                            people = project.members.map { it.toUser() },
                            donePercent = donePercent
                        )
                    }

                    val currentBoard = project.boards.getOrNull(selectedBoardIndex)
                    if (currentBoard != null) {
                        // Filter tasks
                        val filteredTasks = currentBoard.tasks.filter { task ->
                            val matchesSearch = searchQuery.isBlank() ||
                                    task.title.contains(searchQuery, ignoreCase = true) ||
                                    task.key.contains(searchQuery, ignoreCase = true) ||
                                    task.description.contains(searchQuery, ignoreCase = true)

                            val matchesType = selectedTypeFilter == null || task.type == selectedTypeFilter
                            val matchesPriority = selectedPriorityFilter == null || task.priority == selectedPriorityFilter
                            val matchesSprint = when (selectedSprintFilter) {
                                null -> true
                                "BACKLOG" -> task.sprintId == null
                                else -> task.sprintId == selectedSprintFilter
                            }

                            val matchesMine = !onlyMyIssues || task.assignees.any { it.id == appState.currentUser?.id }

                            matchesSearch && matchesType && matchesPriority && matchesSprint && matchesMine
                        }

                        val columns = currentBoard.columns
                        val firstColumnId = columns.firstOrNull()?.id
                        // Tasks pointing at an unknown column fall back to the first column
                        val tasksByColumn = columns.associate { column ->
                            column.id to filteredTasks.filter { task ->
                                if (columns.any { it.id == task.status }) task.status == column.id
                                else column.id == firstColumnId
                            }
                        }

                        fun moveTaskToColumn(droppedTask: TaskDto, newColumnId: String, beforeTaskId: String?) {
                            // Show the result straight away: take the card out and put it where it was dropped
                            project = project.copy(boards = project.boards.map { board ->
                                if (board.id != droppedTask.boardId) return@map board
                                val moved = board.tasks.firstOrNull { it.id == droppedTask.id }?.copy(status = newColumnId) ?: return@map board
                                val rest = board.tasks.filter { it.id != droppedTask.id }
                                val insertAt = rest.indexOfFirst { it.id == beforeTaskId }.takeIf { it >= 0 }
                                    ?: rest.indexOfLast { it.status == newColumnId }.let { if (it >= 0) it + 1 else rest.size }
                                board.copy(tasks = rest.toMutableList().apply { add(insertAt, moved) })
                            })
                            scope.launch {
                                appState.apiClient.moveTask(droppedTask.id, newColumnId, beforeTaskId)
                                refreshProject()
                            }
                        }

                        fun reorderColumn(column: BoardColumnDto, newPosition: Int) {
                            scope.launch {
                                appState.apiClient.updateColumn(project.id, currentBoard.id, column.id, UpdateColumnRequest(position = newPosition))
                                refreshProject()
                            }
                        }

                        // Kanban Columns with Drag & Drop
                        val boardScrollState = rememberScrollState()
                        KanbanDragDropContainer(state = dragDropState, scrollState = boardScrollState) {
                            Row(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .horizontalScroll(boardScrollState)
                                    .padding(start = 28.dp, end = 28.dp, bottom = 20.dp),
                                horizontalArrangement = Arrangement.spacedBy(14.dp)
                            ) {
                                columns.forEachIndexed { index, column ->
                                    KanbanColumn(
                                        column = column,
                                        tasks = tasksByColumn[column.id] ?: emptyList(),
                                        isHovered = dragDropState.hoveredStatus == column.id,
                                        dragDropState = dragDropState,
                                        canMoveLeft = index > 0,
                                        canMoveRight = index < columns.lastIndex,
                                        canDelete = columns.size > 1,
                                        onAddIssue = { openCreateIssue(column.id) },
                                        onEditColumn = { editingColumn = column },
                                        onMoveColumnLeft = { reorderColumn(column, index - 1) },
                                        onMoveColumnRight = { reorderColumn(column, index + 1) },
                                        onDeleteColumn = { deletingColumn = column },
                                        onTaskClick = { task -> editingTask = task },
                                        onDrop = { droppedTask, newColumnId, beforeTaskId -> moveTaskToColumn(droppedTask, newColumnId, beforeTaskId) }
                                    )
                                }
                                AddColumnPlaceholder(onClick = { showAddColumnDialog = true })
                            }
                        }

                        if (showAddColumnDialog) {
                            ColumnEditDialog(
                                title = "Add status",
                                confirmLabel = "Add",
                                initialName = "",
                                initialColor = ColumnColorPalette[columns.size % ColumnColorPalette.size],
                                onDismiss = { showAddColumnDialog = false },
                                onConfirm = { name, color ->
                                    showAddColumnDialog = false
                                    scope.launch {
                                        appState.apiClient.createColumn(project.id, currentBoard.id, CreateColumnRequest(name = name, color = color))
                                        refreshProject()
                                    }
                                }
                            )
                        }

                        editingColumn?.let { column ->
                            ColumnEditDialog(
                                title = "Edit status",
                                confirmLabel = "Save",
                                initialName = column.name,
                                initialColor = column.color,
                                onDismiss = { editingColumn = null },
                                onConfirm = { name, color ->
                                    editingColumn = null
                                    scope.launch {
                                        appState.apiClient.updateColumn(project.id, currentBoard.id, column.id, UpdateColumnRequest(name = name, color = color))
                                        refreshProject()
                                    }
                                }
                            )
                        }

                        deletingColumn?.let { column ->
                            DeleteColumnDialog(
                                column = column,
                                otherColumns = columns.filter { it.id != column.id },
                                taskCount = tasksByColumn[column.id]?.size ?: 0,
                                onDismiss = { deletingColumn = null },
                                onConfirm = { moveTo ->
                                    deletingColumn = null
                                    scope.launch {
                                        appState.apiClient.deleteColumn(project.id, currentBoard.id, column.id, moveTo)
                                        refreshProject()
                                    }
                                }
                            )
                        }
                    } else if (!hasLoaded) {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            androidx.compose.material3.CircularProgressIndicator(color = NibblerColors.Accent)
                        }
                    } else {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Text("No boards in this project.", style = MaterialTheme.typography.titleMedium, color = NibblerColors.OnSurface)
                                Spacer(modifier = Modifier.height(12.dp))
                                Button(
                                    onClick = {
                                        boardName = "Development"
                                        boardDescription = "Development board"
                                        showCreateBoardDialog = true
                                    },
                                    shape = RoundedCornerShape(8.dp),
                                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                                ) {
                                    Text("Create board")
                                }
                            }
                        }
                    }
                }

                ProjectTab.DOCS -> {
                    DocumentViewerScreen(appState = appState, project = project)
                }

                ProjectTab.MEMBERS -> {
                    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Project Members", style = MaterialTheme.typography.titleLarge, color = NibblerColors.OnSurface)
                            Button(
                                onClick = { showAddMemberDialog = true },
                                shape = RoundedCornerShape(8.dp),
                                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                            ) {
                                Text("+ Add Member")
                            }
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            items(project.members) { member ->
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(8.dp),
                                    colors = CardDefaults.cardColors(containerColor = NibblerColors.SurfaceContainer),
                                    border = CardDefaults.outlinedCardBorder().copy(
                                        width = 1.dp,
                                        brush = androidx.compose.ui.graphics.SolidColor(NibblerColors.OutlineVariant)
                                    )
                                ) {
                                    Row(
                                        modifier = Modifier.padding(16.dp).fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column {
                                            Text(member.name, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold, color = NibblerColors.OnSurface)
                                            Text(member.email, style = MaterialTheme.typography.bodySmall, color = NibblerColors.OnSurfaceVariant)
                                        }
                                        Text(member.role.name, color = NibblerColors.Secondary, style = MaterialTheme.typography.labelMedium)
                                    }
                                }
                            }
                        }
                    }
                }

                ProjectTab.SETTINGS -> {
                    ProjectSettingsScreen(
                        appState = appState,
                        project = project,
                        onProjectChanged = { refreshProject() }
                    )
                }
            }
        }

        // Dialog: Create Board
        if (showCreateBoardDialog) {
            AlertDialog(
                onDismissRequest = { showCreateBoardDialog = false },
                containerColor = NibblerColors.SurfaceContainerHigh,
                title = { Text("Create Custom Board", color = NibblerColors.OnSurface) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        OutlinedTextField(
                            value = boardName,
                            onValueChange = { boardName = it },
                            label = { Text("Board Name (e.g. Development, Design, QA)") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )
                        OutlinedTextField(
                            value = boardDescription,
                            onValueChange = { boardDescription = it },
                            label = { Text("Description") },
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (boardName.isNotBlank()) {
                                scope.launch {
                                    appState.apiClient.createBoard(
                                        project.id,
                                        CreateBoardRequest(name = boardName, description = boardDescription, position = project.boards.size)
                                    )
                                    showCreateBoardDialog = false
                                    refreshProject()
                                }
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                    ) { Text("Create") }
                },
                dismissButton = {
                    TextButton(onClick = { showCreateBoardDialog = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
                }
            )
        }

        // Dialog: Edit Board
        if (showEditBoardDialog && project.boards.isNotEmpty()) {
            val currentBoard = project.boards[selectedBoardIndex.coerceIn(0, project.boards.lastIndex)]
            AlertDialog(
                onDismissRequest = { showEditBoardDialog = false },
                containerColor = NibblerColors.SurfaceContainerHigh,
                title = { Text("Edit board: ${currentBoard.name}", color = NibblerColors.OnSurface) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        OutlinedTextField(
                            value = boardName,
                            onValueChange = { boardName = it },
                            label = { Text("Board Name") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )
                        OutlinedTextField(
                            value = boardDescription,
                            onValueChange = { boardDescription = it },
                            label = { Text("Description") },
                            modifier = Modifier.fillMaxWidth()
                        )
                        // Actions sit under the fields so they line up with them
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth().padding(top = 6.dp)) {
                            Button(
                                modifier = Modifier.weight(1f),
                                onClick = {
                                    scope.launch {
                                        appState.apiClient.updateBoard(
                                            project.id,
                                            currentBoard.id,
                                            UpdateBoardRequest(name = boardName, description = boardDescription)
                                        )
                                        showEditBoardDialog = false
                                        refreshProject()
                                    }
                                },
                                shape = RoundedCornerShape(8.dp),
                                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                            ) { Text("Save Changes") }

                            Button(
                                modifier = Modifier.weight(1f),
                                onClick = {
                                    scope.launch {
                                        appState.apiClient.deleteBoard(project.id, currentBoard.id)
                                        showEditBoardDialog = false
                                        selectedBoardIndex = 0
                                        refreshProject()
                                    }
                                },
                                shape = RoundedCornerShape(8.dp),
                                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.ErrorContainer, contentColor = NibblerColors.Error)
                            ) { Text("Delete Board") }
                        }
                        TextButton(
                            onClick = { showEditBoardDialog = false },
                            modifier = Modifier.align(Alignment.End)
                        ) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
                    }
                },
                confirmButton = {},
            )
        }

        // Dialog: Sprints Manager
        if (showSprintManagerDialog) {
            AlertDialog(
                onDismissRequest = { showSprintManagerDialog = false },
                containerColor = NibblerColors.SurfaceContainerHigh,
                title = { Text("Sprints", color = NibblerColors.OnSurface) },
                text = {
                    Column(
                        modifier = Modifier.widthIn(max = 500.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Text("Create a new Sprint with start and end dates:", style = MaterialTheme.typography.bodySmall, color = NibblerColors.OnSurfaceVariant)

                        OutlinedTextField(
                            value = newSprintName,
                            onValueChange = { newSprintName = it },
                            label = { Text("Sprint name") },
                            placeholder = { Text("e.g. Sprint 1") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )
                        OutlinedTextField(
                            value = newSprintGoal,
                            onValueChange = { newSprintGoal = it },
                            label = { Text("Sprint goal") },
                            modifier = Modifier.fillMaxWidth()
                        )
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            OutlinedTextField(
                                value = newSprintStartDate,
                                onValueChange = { newSprintStartDate = it },
                                label = { Text("Start date") },
                                placeholder = { Text("YYYY-MM-DD") },
                                singleLine = true,
                                modifier = Modifier.weight(1f)
                            )
                            OutlinedTextField(
                                value = newSprintEndDate,
                                onValueChange = { newSprintEndDate = it },
                                label = { Text("End date") },
                                placeholder = { Text("YYYY-MM-DD") },
                                singleLine = true,
                                modifier = Modifier.weight(1f)
                            )
                        }

                        Button(
                            onClick = {
                                if (newSprintName.isNotBlank()) {
                                    scope.launch {
                                        appState.apiClient.createSprint(
                                            project.id,
                                            CreateSprintRequest(
                                                name = newSprintName,
                                                goal = newSprintGoal,
                                                startDate = newSprintStartDate,
                                                endDate = newSprintEndDate,
                                                status = SprintStatus.PLANNED
                                            )
                                        )
                                        newSprintName = ""
                                        newSprintGoal = ""
                                        newSprintStartDate = ""
                                        newSprintEndDate = ""
                                        refreshProject()
                                    }
                                }
                            },
                            shape = RoundedCornerShape(8.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("Create sprint")
                        }

                        Spacer(modifier = Modifier.height(8.dp))
                        Text("Existing sprints", style = MaterialTheme.typography.titleSmall, color = NibblerColors.OnSurface)

                        LazyColumn(modifier = Modifier.height(180.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            items(project.sprints) { sprint ->
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(8.dp),
                                    colors = CardDefaults.cardColors(containerColor = NibblerColors.SurfaceContainer),
                                    border = CardDefaults.outlinedCardBorder().copy(
                                        width = 1.dp,
                                        brush = androidx.compose.ui.graphics.SolidColor(NibblerColors.OutlineVariant)
                                    )
                                ) {
                                    Row(
                                        modifier = Modifier.padding(10.dp).fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Text(sprint.name, fontWeight = FontWeight.Bold, color = NibblerColors.OnSurface)
                                                Spacer(modifier = Modifier.width(6.dp))
                                                SprintStatusBadge(sprint.status)
                                            }
                                            Text("${sprint.startDate} ~ ${sprint.endDate}", style = MaterialTheme.typography.labelSmall, color = NibblerColors.OnSurfaceVariant)
                                        }

                                        Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                            if (sprint.status == SprintStatus.PLANNED) {
                                                Button(
                                                    onClick = {
                                                        scope.launch {
                                                            appState.apiClient.updateSprint(project.id, sprint.id, UpdateSprintRequest(status = SprintStatus.ACTIVE))
                                                            refreshProject()
                                                        }
                                                    },
                                                    shape = RoundedCornerShape(8.dp),
                                                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                                                ) { Text("Start", style = MaterialTheme.typography.labelSmall) }
                                            } else if (sprint.status == SprintStatus.ACTIVE) {
                                                Button(
                                                    onClick = {
                                                        scope.launch {
                                                            appState.apiClient.updateSprint(project.id, sprint.id, UpdateSprintRequest(status = SprintStatus.COMPLETED))
                                                            refreshProject()
                                                        }
                                                    },
                                                    shape = RoundedCornerShape(8.dp),
                                                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Success)
                                                ) { Text("Complete", style = MaterialTheme.typography.labelSmall) }
                                            }

                                            OutlinedButton(
                                                onClick = {
                                                    scope.launch {
                                                        appState.apiClient.deleteSprint(project.id, sprint.id)
                                                        refreshProject()
                                                    }
                                                },
                                                shape = RoundedCornerShape(8.dp),
                                                border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
                                            ) { Text("✕", style = MaterialTheme.typography.labelSmall, color = NibblerColors.Error) }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                confirmButton = {
                    TextButton(onClick = { showSprintManagerDialog = false }) { Text("Close", color = NibblerColors.OnSurfaceVariant) }
                }
            )
        }

        // Dialog: Create Task
        if (showCreateTaskDialog && project.boards.isNotEmpty()) {
            val currentBoardId = project.boards[selectedBoardIndex.coerceIn(0, project.boards.lastIndex)].id
            AlertDialog(
                onDismissRequest = { showCreateTaskDialog = false },
                containerColor = NibblerColors.SurfaceContainerHigh,
                title = { Text("Create issue", color = NibblerColors.OnSurface) },
                text = {
                    Column(
                        modifier = Modifier.widthIn(max = 480.dp).verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        OutlinedTextField(
                            value = taskTitle,
                            onValueChange = { taskTitle = it },
                            label = { Text("Summary") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )

                        // Issue Type selector chips
                        Text("Type", style = MaterialTheme.typography.labelMedium, color = NibblerColors.OnSurface)
                        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            IssueType.values().forEach { type ->
                                Box(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(8.dp))
                                        .clickable { taskType = type }
                                        .border(
                                            width = if (taskType == type) 2.dp else 0.dp,
                                            color = if (taskType == type) NibblerColors.ElectricIndigo else Color.Transparent,
                                            shape = RoundedCornerShape(8.dp)
                                        )
                                ) {
                                    IssueTypeBadge(type = type)
                                }
                            }
                        }

                        // Priority selector chips
                        Text("Priority", style = MaterialTheme.typography.labelMedium, color = NibblerColors.OnSurface)
                        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            TaskPriority.values().forEach { priority ->
                                Box(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(8.dp))
                                        .clickable { taskPriority = priority }
                                        .border(
                                            width = if (taskPriority == priority) 2.dp else 0.dp,
                                            color = if (taskPriority == priority) NibblerColors.ElectricIndigo else Color.Transparent,
                                            shape = RoundedCornerShape(8.dp)
                                        )
                                ) {
                                    PriorityBadge(priority = priority)
                                }
                            }
                        }

                        Text("Assignees", style = MaterialTheme.typography.labelMedium, color = NibblerColors.OnSurface)
                        AssigneeSelector(
                            members = project.members,
                            selectedIds = taskAssigneeIds,
                            onChange = { taskAssigneeIds = it }
                        )

                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            OutlinedTextField(
                                value = taskVersion,
                                onValueChange = { taskVersion = it },
                                label = { Text("Fix version") },
                                placeholder = { Text("e.g. v1.0") },
                                singleLine = true,
                                modifier = Modifier.weight(1f)
                            )

                            // Sprint selector dropdown
                            var sprintSelectExpanded by remember { mutableStateOf(false) }
                            Box(modifier = Modifier.weight(1f).padding(top = 8.dp)) {
                                OutlinedButton(
                                    onClick = { sprintSelectExpanded = true },
                                    shape = RoundedCornerShape(8.dp),
                                    border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant),
                                    modifier = Modifier.fillMaxWidth().height(56.dp)
                                ) {
                                    val sprintName = project.sprints.firstOrNull { it.id == taskSprintId }?.name ?: "No sprint"
                                    Text(sprintName, overflow = TextOverflow.Ellipsis, maxLines = 1, color = NibblerColors.OnSurface)
                                }
                                DropdownMenu(
                                    expanded = sprintSelectExpanded,
                                    onDismissRequest = { sprintSelectExpanded = false },
                                    modifier = Modifier.background(NibblerColors.SurfaceContainerHigh)
                                ) {
                                    DropdownMenuItem(
                                        text = { Text("No Sprint (Backlog)", color = NibblerColors.OnSurface) },
                                        onClick = { taskSprintId = null; sprintSelectExpanded = false }
                                    )
                                    project.sprints.forEach { sp ->
                                        DropdownMenuItem(
                                            text = { Text(sp.name, color = NibblerColors.OnSurface) },
                                            onClick = { taskSprintId = sp.id; sprintSelectExpanded = false }
                                        )
                                    }
                                }
                            }
                        }

                        OutlinedTextField(
                            value = taskDescription,
                            onValueChange = { taskDescription = it },
                            label = { Text("Description") },
                            modifier = Modifier.fillMaxWidth().height(120.dp)
                        )
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (taskTitle.isNotBlank()) {
                                scope.launch {
                                    appState.apiClient.createTask(
                                        project.id,
                                        currentBoardId,
                                        CreateTaskRequest(
                                            title = taskTitle,
                                            description = taskDescription,
                                            type = taskType,
                                            status = taskStatus,
                                            priority = taskPriority,
                                            version = taskVersion.ifBlank { null },
                                            sprintId = taskSprintId,
                                            assigneeIds = taskAssigneeIds
                                        )
                                    )
                                    showCreateTaskDialog = false
                                    refreshProject()
                                }
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                    ) { Text("Create issue") }
                },
                dismissButton = {
                    TextButton(onClick = { showCreateTaskDialog = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
                }
            )
        }

        // Dialog: issue details (properties, activity, comments)
        editingTask?.let { task ->
            // Always show the freshest copy of the task from the refreshed project
            val latest = project.boards.flatMap { it.tasks }.firstOrNull { it.id == task.id } ?: task
            TaskDetailDialog(
                task = latest,
                project = project,
                currentUser = appState.currentUser,
                apiClient = appState.apiClient,
                onDismiss = { editingTask = null },
                onChanged = { refreshProject() }
            )
        }

        // Dialog: Add Member
        if (showAddMemberDialog) {
            AlertDialog(
                onDismissRequest = { showAddMemberDialog = false },
                containerColor = NibblerColors.SurfaceContainerHigh,
                title = { Text("Add Project Member", color = NibblerColors.OnSurface) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(
                            value = memberEmail,
                            onValueChange = { memberEmail = it },
                            label = { Text("User Email") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (memberEmail.isNotBlank()) {
                                scope.launch {
                                    appState.apiClient.addMember(
                                        project.id,
                                        AddMemberRequest(email = memberEmail, role = memberRole)
                                    )
                                    showAddMemberDialog = false
                                    memberEmail = ""
                                    refreshProject()
                                }
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                    ) { Text("Add Member") }
                },
                dismissButton = {
                    TextButton(onClick = { showAddMemberDialog = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
                }
            )
        }
    }
}
